<template>
  <teleport to="body">
    <div class="modal">
      <div class="map-modal">
        <HeaderBar>
          <div class="header-contents">
            <div class="title typo-field-text">
              <TargetSvg
                v-if="diagram"
                class="icon icon--auto"
              />
              <SetLocationSvg
                v-if="operationBounds"
                class="icon show-for-medium"
              />
              {{ $t('issues.set_location') }}
              <span class="show-for-medium">{{ operationBounds ? ':' : '' }}</span>
            </div>
            <div
              v-if="operationBounds"
              class="lat-lng typo-small-title"
            >
              <span class="lat">{{ latitude || '-' }}</span>,&nbsp;
              <span class="lng">{{ longitude || '-' }}</span>
            </div>
          </div>
          <template #right>
            <button
              class="button"
              type="button"
              @click="cancel"
            >
              {{ $t('general.cancel') }}
            </button>
            <button
              class="button button--inverse"
              type="button"
              @click="submit"
            >
              <span class="show-for-medium">{{ $t('general.update') }}</span>
              <span class="hide-for-medium">{{ $t('general.done') }}</span>
            </button>
          </template>
        </HeaderBar>

        <div class="map-modal__content">
          <LeafletMap
            ref="map"
            :class="['map-modal__map', { 'out-of-bounds': isOutOfBounds }]"
            :diagram="diagram"
            :operationBounds="operationBounds"
            @drag="onMapMove"
            @init="onMapInit"
            @mapClick="onMapClick"
          />
          <OutOfBoundsMessage v-if="isOutOfBounds" />
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { computed, ref } from 'vue';
import pointInPolygon from '@turf/boolean-point-in-polygon';

import { DotIcon } from '@/vendor/leaflet_mods';

import {
  latLngToGeoJSON,
  latLngToRgeo,
  rgeoToLatLng,
  geoJSONPolygonToLatLngs,
  rgeoPolygonToGeoJSON,
} from '@/functions/geospatial_conversions';
import regionPolygonOptions from '@/functions/region_polygon_options';
import { screenAtLeast } from '@/functions/screen_width';

import HeaderBar from '@/components/generic/header_bar.vue';
import LeafletMap from '@/vendor/components/leaflet_map.vue';
import OutOfBoundsMessage from '@/components/generic/out_of_bounds_message.vue';

import SetLocationSvg from '@/images/set-location.svg';
import TargetSvg from '@/images/target.svg';

const PointIcon = DotIcon.extend({
  options: {
    className: 'map-modal__point',
    iconSize: [9, 9],
    iconAnchor: [5, 5],
  },
});

const props = defineProps({
  operationBounds: { type: String, required: false, default: null },
  diagram: { type: String, required: false, default: null },
  location: { type: String, default: null },
  regions: { type: Array, required: true },
});
const emit = defineEmits(['cancel', 'submit']);

const map = ref(null);
const currentValue = ref(null);
const isOutOfBounds = ref(false);
let marker = null;

const latitude = computed(() => {
  if (!currentValue.value) return null;
  return currentValue.value[0];
});

const longitude = computed(() => {
  if (!currentValue.value) return null;
  return currentValue.value[1];
});

async function onMapInit() {
  if (props.regions) {
    props.regions.forEach(loadRegion);
  }

  if (props.location) {
    let location = rgeoToLatLng(props.location);
    setMarker(...location);
    map.value.goTo(location, 18);
  } else if (!screenAtLeast('medium')) {
    let { lat, lng } = map.value.getCenter();
    setMarker(lat, lng);
  }
}

function loadRegion(region) {
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let polygon = map.value.addPolygon(
    latLngs,
    regionPolygonOptions(region.region_type)
  );
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
    polygon,
  };
  polygon.region = regionObject;
}

function onMapClick({ latlng: { lat, lng } }) {
  if (screenAtLeast('medium')) {
    setMarker(lat, lng);
  }
}

function onMapMove([lat, lng]) {
  if (!screenAtLeast('medium')) {
    setMarker(lat, lng);
  }
}

function setMarker(lat, lng) {
  map.value.enableCrosshair(lat, lng);
  if (!marker) {
    marker = map.value.addMarker([lat, lng], { icon: new PointIcon() });
  } else {
    marker.setLatLng([lat, lng]);
  }
  isOutOfBounds.value = false;
  currentValue.value = [lat, lng];
}

function cancel() {
  emit('cancel');
}

function submit() {
  let point = latLngToGeoJSON(currentValue.value);
  if ((props.operationBounds && outOfBounds()) || (map.value.diagramBoundsValidation(point.coordinates))) {
    isOutOfBounds.value = true;
  } else {
    emit('submit', currentValue.value ? latLngToRgeo(currentValue.value) : null);
  }
}

function outOfBounds() {
  let point = latLngToGeoJSON(currentValue.value);
  let polygon = rgeoPolygonToGeoJSON(props.operationBounds);
  return !pointInPolygon(point, polygon);
}
</script>

<style lang="scss" scoped>
@import '@/stylesheets/global';

.header-contents {
  white-space: nowrap;
  padding-right: bu(10);
  width: 100%;

  svg.icon {
    fill: transparent;
    margin-right: bu(2);
  }

  @include breakpoint(small) {
    padding-right: bu(5);
  }

  @include breakpoint(medium) {
    display: flex;
    align-items: center;
  }
}

.title {
  display: flex;
  align-items: center;
  @include breakpoint(medium) {
    margin-right: bu(1);
  }
}

.lat-lng {
  color: map-get($foreground-color, dark);
  display: flex;
}

.lat, .lng {
  overflow: hidden;
  text-overflow: ellipsis;
}

.map-modal__content {
  position: relative;
}

.out-of-bounds :deep(.region-polygon--operation) {
  fill: map-get($red-color, default);
  stroke: map-get($red-color, default);
}
</style>
